import { useState, useEffect } from "react"
import Modal from "../Modal"
import Button from "../Button"
import api from "../../services/api"

// ListOrganizationsRequest caps per_page at 200.
const ORG_PICKER_PAGE_SIZE = 200

const AssignOrgAdminModal = ({ isOpen, onClose, onSuccess, user }) => {
  const [organizations, setOrganizations] = useState([])
  const [selectedOrgId, setSelectedOrgId] = useState(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!isOpen) return

    setLoading(true)
    setError(null)
    setSelectedOrgId(user?.organization?.id ?? user?.organization_id ?? null)

    api.getAdminOrganizations({ per_page: ORG_PICKER_PAGE_SIZE, sort_dir: 'asc' })
      .then((response) => setOrganizations(response.data.data ?? response.data))
      .catch(() => setError('Failed to load organizations'))
      .finally(() => setLoading(false))
  }, [isOpen])

  const handleConfirm = async () => {
    if (!selectedOrgId) return

    setSaving(true)
    setError(null)

    try {
      await api.adminUpdateUser(user.id, { is_admin: true, organization_id: selectedOrgId })
      onSuccess()
      onClose()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign admin')
    } finally {
      setSaving(false)
    }
  }

  if (!user) return null

  return (
    <Modal isOpen={isOpen} setIsOpen={onClose} width="md:max-w-screen-sm">
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-brand bg-opacity-10 flex items-center justify-center">
            <i className="fa fa-user-shield text-brand text-xl"></i>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Assign Org Admin</h2>
            <p className="text-sm text-gray-500">{user.name}</p>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Select the organization this user will be an admin of.
        </p>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="mb-6">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <i className="fa fa-spinner fa-spin mr-2"></i>
              Loading organizations...
            </div>
          ) : (
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {organizations.map((org) => (
                <label
                  key={org.id}
                  className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 ${
                    selectedOrgId === org.id ? 'bg-brand bg-opacity-5' : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="organization"
                    value={org.id}
                    checked={selectedOrgId === org.id}
                    onChange={() => setSelectedOrgId(org.id)}
                    className="h-4 w-4 border-gray-300"
                  />
                  <span className="text-sm font-medium text-gray-900">{org.name}</span>
                </label>
              ))}
              {organizations.length === 0 && !loading && (
                <p className="text-sm text-gray-500 px-4 py-3">No organizations found</p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="secondary-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={saving || loading || !selectedOrgId}
          >
            {saving ? (
              <>
                <i className="fa fa-spinner fa-spin mr-2"></i>
                Saving...
              </>
            ) : (
              'Assign Admin'
            )}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default AssignOrgAdminModal
